import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Pressable,
  Text,
  type PressableProps,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from "react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

export type BrownButtonShape = "rounded" | "pill";
export type BrownButtonSize = "md" | "compact";
export type BrownButtonVariant = "solid" | "soft" | "outline";

export type BrownButtonProps = Omit<PressableProps, "children" | "style"> & {
  label: string;
  /** Stable analytics identifier, required when `label` is translated. */
  action?: string;
  variant?: BrownButtonVariant;
  size?: BrownButtonSize;
  shape?: BrownButtonShape;
  radius?: number;
  fullWidth?: boolean;
  fill?: boolean;
  width?: number;
  loading?: boolean;
  leading?: ReactNode;
  icon?: ReactNode;
  trailing?: ReactNode;
  disabledOpacity?: number;
  pressedOpacity?: number;
  activityIndicatorColor?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: StyleProp<TextStyle>;
};

const BROWN = "#8B3A1A";
const TEXT = "#2A2522";
const WHITE = "#FFFFFF";

const SHAPE_RADIUS: Record<BrownButtonShape, number> = {
  rounded: 12,
  pill: 999,
};

const VARIANT_STYLE: Record<
  BrownButtonVariant,
  {
    backgroundColor: string;
    borderWidth: number;
    borderColor: string;
    textColor: string;
    activityIndicatorColor: string;
  }
> = {
  solid: {
    backgroundColor: BROWN,
    borderWidth: 0,
    borderColor: "transparent",
    textColor: WHITE,
    activityIndicatorColor: WHITE,
  },
  soft: {
    backgroundColor: "rgba(0,0,0,0.06)",
    borderWidth: 0,
    borderColor: "transparent",
    textColor: TEXT,
    activityIndicatorColor: TEXT,
  },
  outline: {
    backgroundColor: "rgba(0,0,0,0.03)",
    borderWidth: 1,
    borderColor: "rgba(0,0,0,0.06)",
    textColor: BROWN,
    activityIndicatorColor: BROWN,
  },
};

const SIZE_STYLE: Record<
  BrownButtonSize,
  {
    minHeight: number;
    paddingVertical: number;
    paddingHorizontal: number;
    gap: number;
    fontSize: number;
    lineHeight: number;
    fontWeight: TextStyle["fontWeight"];
  }
> = {
  md: {
    minHeight: 48,
    paddingVertical: 14,
    paddingHorizontal: 16,
    gap: 8,
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "600",
  },
  compact: {
    minHeight: 38,
    paddingVertical: 10,
    paddingHorizontal: 13,
    gap: 6,
    fontSize: 12.5,
    lineHeight: 16,
    fontWeight: "800",
  },
};

export function BrownButton({
  label,
  onPress,
  variant = "solid",
  size = "md",
  shape = "rounded",
  radius,
  fullWidth = true,
  fill = false,
  width,
  loading = false,
  leading,
  icon,
  trailing,
  disabled = false,
  disabledOpacity = 0.55,
  pressedOpacity = 0.82,
  activityIndicatorColor,
  action,
  accessibilityLabel,
  style,
  textStyle,
  ...pressableProps
}: BrownButtonProps) {
  const variantStyle = VARIANT_STYLE[variant];
  const sizeStyle = SIZE_STYLE[size];
  const isInactive = disabled || loading;
  const capturePress = usePressCapture("BrownButton");

  return (
    <Pressable
      {...pressableProps}
      onPress={(event) => {
        capturePress(pressLabel(label, accessibilityLabel), action);
        onPress?.(event);
      }}
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityRole="button"
      accessibilityState={{ disabled: isInactive, busy: loading }}
      disabled={isInactive}
      style={({ pressed }) => [
        {
          flexGrow: fill ? 1 : undefined,
          flexBasis: fill ? 0 : undefined,
          width,
          alignSelf: fullWidth ? "stretch" : undefined,
          minHeight: sizeStyle.minHeight,
          borderRadius: radius ?? SHAPE_RADIUS[shape],
          backgroundColor: variantStyle.backgroundColor,
          borderWidth: variantStyle.borderWidth,
          borderColor: variantStyle.borderColor,
          paddingVertical: sizeStyle.paddingVertical,
          paddingHorizontal: sizeStyle.paddingHorizontal,
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: sizeStyle.gap,
          opacity: loading
            ? 0.7
            : disabled
              ? disabledOpacity
              : pressed
                ? pressedOpacity
                : 1,
        },
        style,
      ]}
    >
      {loading ? (
        <ActivityIndicator
          size="small"
          color={activityIndicatorColor ?? variantStyle.activityIndicatorColor}
        />
      ) : (
        (leading ?? icon)
      )}
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={[
          {
            fontSize: sizeStyle.fontSize,
            lineHeight: sizeStyle.lineHeight,
            fontWeight: sizeStyle.fontWeight,
            color: variantStyle.textColor,
            textAlign: "center",
          },
          textStyle,
        ]}
      >
        {label}
      </Text>
      {trailing}
    </Pressable>
  );
}

export default BrownButton;
